import React, { useState, useEffect, useContext, useMemo } from 'react';
import axios from 'axios';
import { ChevronRight, ChevronDown } from 'lucide-react';
import { AppContext } from '../App';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const translations = {
  en: {
    categories: 'Categories',
    allCategories: 'All Categories'
  },
  tr: {
    categories: 'Kategoriler',
    allCategories: 'Tüm Kategoriler'
  },
  el: {
    categories: 'Κατηγορίες',
    allCategories: 'Όλες οι Κατηγορίες'
  },
  de: {
    categories: 'Kategorien',
    allCategories: 'Alle Kategorien'
  }
};

const CategoryMenu = ({ onCategorySelect, selectedCategory }) => {
  const { language } = useContext(AppContext);
  const t = translations[language] || translations.en;
  const [categories, setCategories] = useState([]);
  const [openCategories, setOpenCategories] = useState(new Set());
  const [categoryProductCounts, setCategoryProductCounts] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch categories
        const catResponse = await axios.get(`${API}/categories`);
        setCategories(catResponse.data);
        
        // Fetch product counts per category
        const prodResponse = await axios.get(`${API}/products`, {
          params: { limit: 10000, active_only: true }
        });
        
        const products = prodResponse.data.products || [];
        const counts = {};
        
        // Count products per category slug
        products.forEach(product => {
          if (product.categories && Array.isArray(product.categories)) {
            product.categories.forEach(catSlug => {
              counts[catSlug] = (counts[catSlug] || 0) + 1;
            });
          }
        });
        
        setCategoryProductCounts(counts);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    
    fetchData();
  }, []);

  // Helper function to get children of a category
  const getChildren = (parentId, allCategories) => {
    return allCategories.filter(cat => cat.parent_id === parentId);
  };

  // Calculate total product count for a category (including all descendants)
  const calculateTotalCount = (category, allCategories, counts) => {
    let count = counts[category.slug] || 0;
    const children = getChildren(category.id, allCategories);
    children.forEach(child => {
      count += calculateTotalCount(child, allCategories, counts);
    });
    return count;
  };

  // Memoized category data with product counts and visibility
  const processedCategories = useMemo(() => {
    if (categories.length === 0) return { parents: [], countMap: {}, visibilityMap: {} };
    
    const countMap = {};
    const visibilityMap = {};
    
    // Calculate total counts for all categories
    categories.forEach(cat => {
      countMap[cat.id] = calculateTotalCount(cat, categories, categoryProductCounts);
    });
    
    // Determine visibility (has products directly or through children)
    categories.forEach(cat => {
      visibilityMap[cat.id] = countMap[cat.id] > 0;
    });
    
    // Get parent categories that have products
    const parents = categories
      .filter(cat => (cat.level === 0 || !cat.parent_id) && visibilityMap[cat.id]);
    
    return { parents, countMap, visibilityMap };
  }, [categories, categoryProductCounts]);

  // Check if category is a leaf (no children with products)
  const isLeafCategory = (category) => {
    const children = getChildren(category.id, categories);
    if (children.length === 0) return true;
    // Has children but none with products = treat as leaf
    const childrenWithProducts = children.filter(c => processedCategories.visibilityMap[c.id]);
    return childrenWithProducts.length === 0;
  };

  const handleCategoryClick = (category) => {
    const children = getChildren(category.id, categories);
    const nonEmptyChildren = children.filter(c => processedCategories.visibilityMap[c.id]);
    const hasNonEmptyChildren = nonEmptyChildren.length > 0;
    const isLeaf = isLeafCategory(category);

    // Always filter products by selected category
    onCategorySelect(category.slug);

    // If this is a leaf category, scroll to top
    if (isLeaf) {
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    }

    if (hasNonEmptyChildren) {
      // Toggle open/close for categories with children
      setOpenCategories(prev => {
        const newSet = new Set(prev);
        if (newSet.has(category.id)) {
          newSet.delete(category.id);
        } else {
          // If this is a primary category (level 0), close all others
          if (category.level === 0) {
            newSet.clear();
          }
          newSet.add(category.id);
        }
        return newSet;
      });
    }
  };

  const renderCategoryWithChildren = (category, level = 0) => {
    // Skip categories with no products
    if (!processedCategories.visibilityMap[category.id]) {
      return null;
    }

    const children = getChildren(category.id, categories);
    const nonEmptyChildren = children.filter(c => processedCategories.visibilityMap[c.id]);
    const hasNonEmptyChildren = nonEmptyChildren.length > 0;
    const isOpen = openCategories.has(category.id);
    const productCount = processedCategories.countMap[category.id] || 0;

    return (
      <div key={category.id}>
        <button
          onClick={() => handleCategoryClick(category)}
          className={`w-full text-left px-4 py-3 hover:bg-[#C8A951] hover:text-white transition-colors flex items-center justify-between ${
            selectedCategory === category.slug ? 'bg-[#C8A951] text-white' : ''
          }`}
          style={{ paddingLeft: `${(level * 20) + 16}px` }}
        >
          <span className="flex items-center gap-2">
            {category[`name_${language}`] || category.name}
            <span className={`text-xs px-1.5 py-0.5 rounded ${
              selectedCategory === category.slug 
                ? 'bg-white/20 text-white' 
                : 'bg-gray-100 text-gray-500'
            }`}>
              {productCount}
            </span>
          </span>
          {hasNonEmptyChildren && (
            isOpen 
              ? <ChevronDown size={16} className="transition-transform" />
              : <ChevronRight size={16} className="transition-transform" />
          )}
        </button>

        {/* Submenu - inline dropdown with non-empty children only */}
        {hasNonEmptyChildren && isOpen && (
          <div className="bg-gray-50">
            {nonEmptyChildren.map(child => renderCategoryWithChildren(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
      <div className="bg-[#1F2D50] text-white px-4 py-3 rounded-t-lg font-bold">
        {t.categories}
      </div>
      <div className="py-2">
        <button
          onClick={() => {
            onCategorySelect('');
            setOpenCategories(new Set());
          }}
          className={`w-full text-left px-4 py-3 hover:bg-[#C8A951] hover:text-white transition-colors ${
            selectedCategory === '' ? 'bg-[#C8A951] text-white' : ''
          }`}
        >
          {t.allCategories}
        </button>
        {processedCategories.parents.map(category => renderCategoryWithChildren(category))}
      </div>
    </div>
  );
};

export default CategoryMenu;
